import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import { useState } from "react";
import { z } from "zod/v4";
import { getDb } from "@/db";
import { events } from "@/db/schema";
import { getSession } from "@/lib/auth-session";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

const createEventSchema = z.object({
	name: z.string().min(1),
	date: z.string().min(1),
	location: z.string().optional(),
	maxWeightOz: z.number().positive(),
	weightUnit: z.enum(["oz", "g"]),
	laneCount: z.number().int().min(1).max(8),
	scoringMethod: z.enum(["best", "average", "drop_worst"]),
	attemptsAllowed: z.number().int().min(1),
	visibility: z.enum(["public", "unlisted", "draft"]),
	registrationMode: z.enum(["open", "admin_only"]),
	carNumberMode: z.enum(["racer_choice", "auto", "admin_only"]),
	requireCheckin: z.boolean(),
	finalsCarCount: z.number().int().min(0).optional(),
	finalsScoringMethod: z.string().optional(),
	finalsRounds: z.number().int().min(1).optional(),
	divisions: z.array(z.string()).optional(),
	racesPerCar: z.number().int().min(1).optional(),
});

const createEvent = createServerFn({ method: "POST" })
	.inputValidator((data: z.infer<typeof createEventSchema>) =>
		createEventSchema.parse(data),
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		const result = await getDb()
			.insert(events)
			.values({
				name: data.name,
				date: data.date,
				location: data.location || null,
				maxWeightOz: data.maxWeightOz,
				weightUnit: data.weightUnit,
				laneCount: data.laneCount,
				scoringMethod: data.scoringMethod,
				attemptsAllowed: data.attemptsAllowed,
				visibility: data.visibility,
				registrationMode: data.registrationMode,
				carNumberMode: data.carNumberMode,
				requireCheckin: data.requireCheckin,
				finalsCarCount: data.finalsCarCount || null,
				finalsScoringMethod: data.finalsScoringMethod || null,
				finalsRounds: data.finalsRounds ?? 1,
				racesPerCar: data.racesPerCar ?? null,
				divisions: JSON.stringify(data.divisions ?? []),
			})
			.returning();
		return result[0];
	});

export const Route = createFileRoute("/admin/events/new")({
	component: NewEventPage,
});

function NewEventPage() {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [weightUnit, setWeightUnit] = useState<"oz" | "g">("oz");
	const [showFinals, setShowFinals] = useState(false);
	const [showDivisions, setShowDivisions] = useState(false);
	const [divisions, setDivisions] = useState<string[]>([]);
	const [newDivision, setNewDivision] = useState("");

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setError("");
		setLoading(true);

		const form = new FormData(e.currentTarget);
		try {
			const finalsCarCountStr = form.get("finalsCarCount") as string;
			const racesPerCarStr = form.get("racesPerCar") as string;
			const event = await createEvent({
				data: {
					name: form.get("name") as string,
					date: form.get("date") as string,
					location: (form.get("location") as string) || undefined,
					maxWeightOz: Number(form.get("maxWeightOz")),
					weightUnit: form.get("weightUnit") as "oz" | "g",
					laneCount: Number(form.get("laneCount")),
					scoringMethod: form.get("scoringMethod") as
						| "best"
						| "average"
						| "drop_worst",
					attemptsAllowed: Number(form.get("attemptsAllowed")),
					visibility:
						(form.get("visibility") as "public" | "unlisted" | "draft") ||
						"public",
					registrationMode:
						(form.get("registrationMode") as "open" | "admin_only") || "open",
					carNumberMode:
						(form.get("carNumberMode") as
							| "racer_choice"
							| "auto"
							| "admin_only") || "racer_choice",
					requireCheckin: form.get("requireCheckin") === "on",
					finalsCarCount:
						showFinals && finalsCarCountStr
							? Number(finalsCarCountStr)
							: undefined,
					finalsScoringMethod: showFinals
						? (form.get("finalsScoringMethod") as string) || undefined
						: undefined,
					finalsRounds: showFinals
						? Number(form.get("finalsRounds") || "1")
						: undefined,
					racesPerCar: racesPerCarStr ? Number(racesPerCarStr) : undefined,
					divisions: divisions.length > 0 ? divisions : undefined,
				},
			});
			navigate({
				to: "/admin/events/$eventId",
				params: { eventId: String(event.id) },
			});
		} catch {
			setError("Failed to create event");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="min-h-screen bg-background p-6">
			<div className="max-w-2xl mx-auto">
				<h1 className="text-4xl text-foreground mb-8">New Event</h1>

				<form
					onSubmit={handleSubmit}
					className="bg-card rounded-xl p-6 space-y-5"
				>
					{error && (
						<div className="bg-destructive/30 border border-destructive text-foreground text-sm rounded-lg p-3">
							{error}
						</div>
					)}

					<div>
						<label className="block text-sm mb-1">Event Name</label>
						<input
							name="name"
							type="text"
							required
							className="w-full px-4 py-2"
							placeholder="2026 Pack 42 Pinewood Derby"
						/>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm mb-1">Date</label>
							<input
								name="date"
								type="date"
								required
								className="w-full px-4 py-2"
							/>
						</div>
						<div>
							<label className="block text-sm mb-1">Location</label>
							<input
								name="location"
								type="text"
								className="w-full px-4 py-2"
								placeholder="Community Center"
							/>
						</div>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm mb-1">Number of Lanes</label>
							<input
								name="laneCount"
								type="number"
								min="1"
								max="8"
								defaultValue="4"
								required
								className="w-full px-4 py-2"
							/>
						</div>
						<div>
							<label className="block text-sm mb-1">Max Weight</label>
							<div className="flex gap-2">
								<input
									name="maxWeightOz"
									type="number"
									step="0.1"
									min="0.1"
									key={weightUnit}
									defaultValue={weightUnit === "oz" ? "5.0" : "141.7"}
									required
									className="flex-1 px-4 py-2"
								/>
								<select
									name="weightUnit"
									value={weightUnit}
									onChange={(e) => setWeightUnit(e.target.value as "oz" | "g")}
									className="px-3 py-2"
								>
									<option value="oz">oz</option>
									<option value="g">g</option>
								</select>
							</div>
						</div>
					</div>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm mb-1">Scoring Method</label>
							<select
								name="scoringMethod"
								defaultValue="best"
								className="w-full px-4 py-2"
							>
								<option value="best">Best Time</option>
								<option value="average">Average Time</option>
								<option value="drop_worst">Drop Worst</option>
							</select>
							<p className="text-muted-foreground text-xs mt-1">
								How each car's final score is calculated from their runs
							</p>
						</div>
						<div>
							<label className="block text-sm mb-1">Attempts Allowed</label>
							<input
								name="attemptsAllowed"
								type="number"
								min="1"
								max="20"
								defaultValue="3"
								required
								className="w-full px-4 py-2"
							/>
							<p className="text-muted-foreground text-xs mt-1">
								Runs per car (typically = lanes x rounds)
							</p>
						</div>
					</div>

					<div>
						<label className="block text-sm mb-1">Races per Car</label>
						<input
							name="racesPerCar"
							type="number"
							min="1"
							max="24"
							placeholder="All lanes"
							className="w-full sm:w-48 px-4 py-2"
						/>
						<p className="text-muted-foreground text-xs mt-1">
							Leave blank for full rotation (every lane)
						</p>
					</div>

					{/* Registration & Visibility */}
					<div className="border-t border-border pt-4 mt-2">
						<p className="text-sm font-bold text-muted-foreground uppercase mb-3">
							Registration
						</p>
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
							<div>
								<label className="block text-sm mb-1">Visibility</label>
								<select
									name="visibility"
									defaultValue="public"
									className="w-full px-4 py-2"
								>
									<option value="public">Public</option>
									<option value="unlisted">Unlisted (link only)</option>
									<option value="draft">Draft (admin only)</option>
								</select>
							</div>
							<div>
								<label className="block text-sm mb-1">Registration</label>
								<select
									name="registrationMode"
									defaultValue="open"
									className="w-full px-4 py-2"
								>
									<option value="open">Open (self-register)</option>
									<option value="admin_only">Admin Only</option>
								</select>
							</div>
							<div>
								<label className="block text-sm mb-1">Car Numbers</label>
								<select
									name="carNumberMode"
									defaultValue="racer_choice"
									className="w-full px-4 py-2"
								>
									<option value="racer_choice">Racer's Choice</option>
									<option value="auto">Auto-Assign</option>
									<option value="admin_only">Admin Only</option>
								</select>
							</div>
						</div>
						<label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
							<input
								type="checkbox"
								name="requireCheckin"
								className="rounded"
							/>
							Require check-in before cars appear in schedule
						</label>
					</div>

					{/* Divisions */}
					<div className="border-t border-border pt-4 mt-2">
						<button
							type="button"
							onClick={() => setShowDivisions(!showDivisions)}
							className="flex items-center gap-2 text-sm font-bold text-muted-foreground uppercase hover:text-foreground transition-colors"
						>
							{showDivisions ? (
								<ChevronDown size={14} />
							) : (
								<ChevronRight size={14} />
							)}
							Divisions
						</button>
						{showDivisions && (
							<div className="mt-3">
								<p className="text-muted-foreground text-xs mb-2">
									Separate leaderboards and schedules per group (e.g., age groups, dens)
								</p>
								<div className="flex flex-wrap gap-2 mb-2">
									{divisions.map((div) => (
										<span
											key={div}
											className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary text-sm font-bold rounded-full"
										>
											{div}
											<button
												type="button"
												onClick={() =>
													setDivisions((d) => d.filter((x) => x !== div))
												}
												className="p-0.5 hover:bg-primary/20 rounded-full transition-colors"
											>
												<X size={12} />
											</button>
										</span>
									))}
								</div>
								<div className="flex gap-2">
									<input
										type="text"
										value={newDivision}
										onChange={(e) => setNewDivision(e.target.value)}
										placeholder="e.g., Tigers"
										className="flex-1 px-3 py-1.5 text-sm"
										onKeyDown={(e) => {
											if (e.key === "Enter") {
												e.preventDefault();
												const val = newDivision.trim();
												if (val && !divisions.includes(val)) {
													setDivisions((d) => [...d, val]);
													setNewDivision("");
												}
											}
										}}
									/>
									<button
										type="button"
										onClick={() => {
											const val = newDivision.trim();
											if (val && !divisions.includes(val)) {
												setDivisions((d) => [...d, val]);
												setNewDivision("");
											}
										}}
										className="px-3 py-1.5 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg text-sm font-bold"
									>
										<Plus size={14} />
									</button>
								</div>
							</div>
						)}
					</div>

					{/* Finals */}
					<div className="border-t border-border pt-4 mt-2">
						<button
							type="button"
							onClick={() => setShowFinals(!showFinals)}
							className="flex items-center gap-2 text-sm font-bold text-muted-foreground uppercase hover:text-foreground transition-colors"
						>
							{showFinals ? (
								<ChevronDown size={14} />
							) : (
								<ChevronRight size={14} />
							)}
							Finals Round
						</button>
						{showFinals && (
							<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
								<div>
									<label className="block text-sm mb-1">Top N Cars</label>
									<input
										name="finalsCarCount"
										type="number"
										min="2"
										max="20"
										defaultValue="4"
										className="w-full px-4 py-2"
									/>
									<p className="text-muted-foreground text-xs mt-1">
										Cars advancing from qualifying
									</p>
								</div>
								<div>
									<label className="block text-sm mb-1">Finals Scoring</label>
									<select
										name="finalsScoringMethod"
										className="w-full px-4 py-2"
									>
										<option value="">Same as qualifying</option>
										<option value="best">Best Time</option>
										<option value="average">Average</option>
										<option value="points">Points</option>
									</select>
								</div>
								<div>
									<label className="block text-sm mb-1">Finals Rounds</label>
									<select
										name="finalsRounds"
										defaultValue="1"
										className="w-full px-4 py-2"
									>
										<option value="1">1</option>
										<option value="2">2</option>
										<option value="3">3</option>
									</select>
									<p className="text-muted-foreground text-xs mt-1">
										Full rotations through all lanes
									</p>
								</div>
							</div>
						)}
					</div>

					<div className="flex gap-3 pt-2">
						<button
							type="submit"
							disabled={loading}
							className="flex-1 py-3 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors uppercase"
						>
							{loading ? "Creating..." : "Create Event"}
						</button>
						<button
							type="button"
							onClick={() => navigate({ to: "/admin/events" })}
							className="px-6 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground font-bold rounded-lg transition-colors uppercase"
						>
							Cancel
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
